<template>
  <v-main class="login-page">
    <div class="ambient ambient-one"></div>
    <div class="ambient ambient-two"></div>
    <div class="grid-overlay"></div>

    <v-container fluid class="login-shell">
      <section class="console-intro">
        <div class="brand-line">
          <div class="brand-mark">
            <v-icon size="28">mdi-robot-industrial</v-icon>
          </div>
          <div>
            <div class="brand-name">SERVO LAB</div>
            <div class="brand-subtitle">舵机运动曲线调试系统</div>
          </div>
        </div>

        <div class="servo-visual" aria-hidden="true">
          <div class="servo-orbit orbit-outer"></div>
          <div class="servo-orbit orbit-inner"></div>
          <div class="servo-core">
            <v-icon size="72" color="primary">mdi-axis-z-rotate-clockwise</v-icon>
          </div>
          <span class="axis-line axis-x"></span>
          <span class="axis-line axis-y"></span>
          <span class="telemetry telemetry-angle">ANGLE&nbsp;&nbsp;090.0°</span>
          <span class="telemetry telemetry-speed">SPEED&nbsp;&nbsp;120°/s</span>
        </div>

        <div class="intro-copy">
          <div class="eyebrow">CONTROL · SIMULATE · VALIDATE</div>
          <h1>精确控制每一次转动</h1>
          <p>登录后进入舵机调试工作台，配置运动轨迹、验证角度曲线并管理操作记录。</p>
          <div class="system-status">
            <span class="status-dot"></span>
            <span>认证服务已连接</span>
            <span class="status-divider"></span>
            <span>SM2 安全传输</span>
          </div>
        </div>
      </section>

      <v-card class="login-card" elevation="16">
        <v-card-text class="login-card-content">
          <div class="login-heading">
            <div>
              <div class="login-kicker">WELCOME BACK</div>
              <h2>登录调试台</h2>
            </div>
            <div class="login-heading-actions">
              <v-select
                v-model="selectedEnvironmentUrl"
                :items="envList"
                item-title="name"
                item-value="manager_url"
                label="环境选择"
                prepend-inner-icon="mdi-earth"
                variant="outlined"
                density="compact"
                hide-details
                :disabled="configLoading || captchaLoading || submitting"
                class="environment-select"
                @update:model-value="handleEnvironmentChange"
              />
              <v-chip color="primary" variant="outlined" size="small">
                <v-icon start size="14">mdi-shield-check-outline</v-icon>
                安全登录
              </v-chip>
            </div>
          </div>

          <v-form ref="formRef" @submit.prevent="submitLogin">
            <label class="field-label" for="username">用户名或手机号</label>
            <v-text-field
              id="username"
              v-model.trim="form.username"
              placeholder="请输入用户名或手机号"
              prepend-inner-icon="mdi-account-outline"
              variant="outlined"
              density="comfortable"
              autocomplete="username"
              :disabled="submitting"
              :error-messages="errors.username"
              class="login-field"
              @update:model-value="errors.username = ''"
            />

            <label class="field-label" for="password">密码</label>
            <v-text-field
              id="password"
              v-model="form.password"
              placeholder="请输入密码"
              prepend-inner-icon="mdi-lock-outline"
              :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              :type="showPassword ? 'text' : 'password'"
              variant="outlined"
              density="comfortable"
              autocomplete="current-password"
              :disabled="submitting"
              :error-messages="errors.password"
              class="login-field"
              @click:append-inner="showPassword = !showPassword"
              @update:model-value="errors.password = ''"
            />

            <label class="field-label" for="captcha">图形验证码</label>
            <div class="captcha-row">
              <v-text-field
                id="captcha"
                v-model.trim="form.captcha"
                placeholder="请输入验证码"
                prepend-inner-icon="mdi-shield-key-outline"
                variant="outlined"
                density="comfortable"
                autocomplete="off"
                :disabled="submitting"
                :error-messages="errors.captcha"
                class="login-field captcha-field"
                @update:model-value="errors.captcha = ''"
              />
              <button
                type="button"
                class="captcha-box"
                :disabled="captchaLoading || submitting"
                aria-label="刷新验证码"
                @click="loadCaptcha"
              >
                <v-progress-circular v-if="captchaLoading" indeterminate color="primary" size="25" width="2" />
                <img v-else-if="captchaImageUrl" :src="captchaImageUrl" alt="验证码" />
                <span v-else>点击刷新</span>
              </button>
            </div>

            <v-alert
              v-if="errorMessage"
              type="error"
              variant="tonal"
              density="compact"
              class="mb-5"
              closable
              @click:close="errorMessage = ''"
            >
              {{ errorMessage }}
            </v-alert>

            <v-btn
              type="submit"
              color="primary"
              size="large"
              block
              :loading="submitting"
              :disabled="configLoading || captchaLoading"
              class="login-button"
            >
              进入调试台
              <v-icon end>mdi-arrow-right</v-icon>
            </v-btn>
          </v-form>

          <div class="login-footer">
            <v-icon size="15">mdi-information-outline</v-icon>
            <span>账号体系与智控台保持一致</span>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script setup>
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import envList from '../envList.json'
import {
  fetchCaptcha,
  fetchPublicConfig,
  loginWithPassword,
  saveAuthSession
} from '../api/auth.js'

const emit = defineEmits(['login-success'])

const formRef = ref(null)
const publicKey = ref('')
const captchaId = ref('')
const captchaImageUrl = ref('')
const configLoading = ref(true)
const captchaLoading = ref(true)
const submitting = ref(false)
const showPassword = ref(false)
const errorMessage = ref('')
const defaultEnvironment = envList[0] || {}
const savedEnvironmentUrl = localStorage.getItem('selectedEnvManagerUrl')
const selectedEnvironmentUrl = ref(
  envList.some(env => env.manager_url === savedEnvironmentUrl)
    ? savedEnvironmentUrl
    : defaultEnvironment.manager_url || ''
)

if (selectedEnvironmentUrl.value) {
  localStorage.setItem('selectedEnvManagerUrl', selectedEnvironmentUrl.value)
}

const form = reactive({
  username: '',
  password: '',
  captcha: ''
})

const errors = reactive({
  username: '',
  password: '',
  captcha: ''
})

function releaseCaptchaImage() {
  if (captchaImageUrl.value) {
    URL.revokeObjectURL(captchaImageUrl.value)
    captchaImageUrl.value = ''
  }
}

async function loadCaptcha() {
  captchaLoading.value = true
  errorMessage.value = ''
  releaseCaptchaImage()

  try {
    const captcha = await fetchCaptcha()
    captchaId.value = captcha.captchaId
    captchaImageUrl.value = captcha.imageUrl
  } catch (error) {
    errorMessage.value = '验证码加载失败，请检查认证服务后重试'
  } finally {
    captchaLoading.value = false
  }
}

async function loadLoginResources() {
  configLoading.value = true
  errorMessage.value = ''
  publicKey.value = ''

  const results = await Promise.allSettled([fetchPublicConfig(), loadCaptcha()])
  if (results[0].status === 'fulfilled') {
    publicKey.value = results[0].value.sm2PublicKey
  } else {
    errorMessage.value = results[0].reason?.message || '登录配置加载失败'
  }
  configLoading.value = false
}

async function handleEnvironmentChange(managerUrl) {
  const environment = envList.find(env => env.manager_url === managerUrl) || defaultEnvironment
  if (!environment.manager_url) return

  selectedEnvironmentUrl.value = environment.manager_url
  localStorage.setItem('selectedEnvManagerUrl', environment.manager_url)
  form.captcha = ''
  await loadLoginResources()
}

function validateForm() {
  errors.username = form.username ? '' : '请输入用户名或手机号'
  errors.password = form.password ? '' : '请输入密码'
  errors.captcha = form.captcha ? '' : '请输入验证码'
  return !errors.username && !errors.password && !errors.captcha
}

async function submitLogin() {
  errorMessage.value = ''
  if (!validateForm()) return
  if (!publicKey.value) {
    errorMessage.value = '登录配置尚未就绪，请稍后重试'
    return
  }

  submitting.value = true
  try {
    const session = await loginWithPassword({
      username: form.username,
      password: form.password,
      captcha: form.captcha,
      captchaId: captchaId.value,
      publicKey: publicKey.value
    })
    const loginEnvironment = envList.find(env => env.manager_url === selectedEnvironmentUrl.value)
    const authenticatedSession = {
      ...session,
      loginEnvironmentUrl: selectedEnvironmentUrl.value,
      loginEnvironmentName: loginEnvironment?.name || ''
    }
    saveAuthSession(authenticatedSession)
    emit('login-success', authenticatedSession)
  } catch (error) {
    const loginError = error.response?.data?.msg || error.message || '登录失败，请检查输入信息'
    form.captcha = ''
    await loadCaptcha()
    errorMessage.value = loginError
  } finally {
    submitting.value = false
  }
}

onMounted(loadLoginResources)

onBeforeUnmount(releaseCaptchaImage)
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: #090d18;
  color: #f3f6ff;
}

.login-shell {
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(520px, 1.1fr) minmax(420px, 0.9fr);
  align-items: center;
  gap: clamp(40px, 7vw, 120px);
  max-width: 1440px;
  padding: 48px clamp(48px, 7vw, 112px);
}

.grid-overlay {
  position: absolute;
  inset: 0;
  opacity: 0.22;
  background-image:
    linear-gradient(rgba(121, 134, 203, 0.14) 1px, transparent 1px),
    linear-gradient(90deg, rgba(121, 134, 203, 0.14) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: linear-gradient(to right, black, transparent 72%);
}

.ambient {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  pointer-events: none;
}

.ambient-one {
  width: 520px;
  height: 520px;
  left: -170px;
  top: 12%;
  background: rgba(63, 81, 181, 0.28);
}

.ambient-two {
  width: 420px;
  height: 420px;
  right: -150px;
  bottom: -140px;
  background: rgba(103, 58, 183, 0.2);
}

.console-intro {
  min-width: 0;
}

.brand-line {
  display: flex;
  align-items: center;
  gap: 14px;
}

.brand-mark {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  color: #b7c4ff;
  border: 1px solid rgba(139, 157, 255, 0.45);
  border-radius: 12px;
  background: rgba(57, 67, 122, 0.28);
  box-shadow: inset 0 0 24px rgba(83, 109, 254, 0.12);
}

.brand-name {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.18em;
}

.brand-subtitle {
  margin-top: 3px;
  color: #8790aa;
  font-size: 12px;
}

.servo-visual {
  position: relative;
  width: min(430px, 38vw);
  aspect-ratio: 1;
  margin: 18px auto 4px;
}

.servo-orbit,
.servo-core,
.axis-line,
.telemetry {
  position: absolute;
}

.servo-orbit {
  inset: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
}

.orbit-outer {
  width: 74%;
  height: 74%;
  border: 1px dashed rgba(122, 142, 255, 0.45);
  animation: orbit 36s linear infinite;
}

.orbit-outer::before,
.orbit-outer::after {
  content: '';
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #8294ff;
  box-shadow: 0 0 18px #6e86ff;
}

.orbit-outer::before {
  top: 13%;
  left: 14%;
}

.orbit-outer::after {
  right: 8%;
  bottom: 22%;
}

.orbit-inner {
  width: 48%;
  height: 48%;
  border: 1px solid rgba(120, 143, 255, 0.23);
  box-shadow: 0 0 60px rgba(77, 103, 239, 0.12);
}

.servo-core {
  inset: 50%;
  width: 118px;
  height: 118px;
  transform: translate(-50%, -50%);
  display: grid;
  place-items: center;
  border: 1px solid rgba(154, 167, 255, 0.48);
  border-radius: 24px;
  background: linear-gradient(145deg, rgba(31, 39, 70, 0.96), rgba(14, 20, 39, 0.96));
  box-shadow: 0 22px 55px rgba(0, 0, 0, 0.42), inset 0 0 35px rgba(88, 111, 255, 0.12);
}

.axis-line {
  left: 50%;
  top: 50%;
  width: 82%;
  height: 1px;
  transform: translate(-50%, -50%);
  background: linear-gradient(90deg, transparent, rgba(111, 133, 255, 0.25), transparent);
}

.axis-y {
  transform: translate(-50%, -50%) rotate(90deg);
}

.telemetry {
  padding: 7px 10px;
  color: #98a8ee;
  font-family: Consolas, monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  border-left: 2px solid #667eea;
  background: rgba(17, 24, 46, 0.76);
}

.telemetry-angle {
  left: 2%;
  top: 26%;
}

.telemetry-speed {
  right: 0;
  bottom: 25%;
}

.eyebrow,
.login-kicker {
  color: #8fa2ff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2em;
}

.intro-copy h1 {
  margin: 11px 0 12px;
  font-size: clamp(34px, 4vw, 54px);
  line-height: 1.1;
  letter-spacing: -0.04em;
}

.intro-copy p {
  max-width: 560px;
  margin: 0;
  color: #929bb3;
  font-size: 15px;
  line-height: 1.8;
}

.system-status {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 24px;
  color: #7e889f;
  font-size: 11px;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #5dd6a4;
  box-shadow: 0 0 10px rgba(93, 214, 164, 0.8);
}

.status-divider {
  width: 1px;
  height: 13px;
  margin: 0 4px;
  background: rgba(255, 255, 255, 0.14);
}

.login-card {
  width: 100%;
  max-width: 500px;
  justify-self: end;
  border: 1px solid rgba(133, 148, 214, 0.2);
  border-radius: 20px !important;
  background: rgba(19, 24, 42, 0.92) !important;
  backdrop-filter: blur(20px);
  box-shadow: 0 32px 90px rgba(0, 0, 0, 0.45) !important;
}

.login-card-content {
  padding: 42px !important;
}

.login-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 34px;
}

.login-heading h2 {
  margin: 7px 0 0;
  font-size: 29px;
  line-height: 1.2;
}

.login-heading-actions {
  width: 168px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.environment-select {
  width: 100%;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #b5bdd0;
  font-size: 12px;
}

.login-field {
  margin-bottom: 6px;
}

.captcha-row {
  display: grid;
  grid-template-columns: 1fr 132px;
  align-items: start;
  gap: 12px;
}

.captcha-box {
  height: 48px;
  display: grid;
  place-items: center;
  overflow: hidden;
  color: #8992a9;
  font-size: 12px;
  border: 1px solid rgba(154, 164, 196, 0.42);
  border-radius: 6px;
  background: #eef1f8;
  cursor: pointer;
  transition: border-color 0.2s, transform 0.2s;
}

.captcha-box:hover:not(:disabled) {
  border-color: #8195ff;
  transform: translateY(-1px);
}

.captcha-box img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.login-button {
  height: 50px !important;
  letter-spacing: 0.06em;
  box-shadow: 0 12px 28px rgba(83, 109, 254, 0.22);
}

.login-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 24px;
  color: #747d94;
  font-size: 11px;
}

@keyframes orbit {
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

@media (max-width: 1050px) {
  .login-shell {
    grid-template-columns: 1fr;
    max-width: 620px;
    padding: 36px 24px;
  }

  .console-intro {
    display: none;
  }

  .login-card {
    justify-self: center;
  }
}

@media (max-width: 560px) {
  .login-shell {
    padding: 18px;
  }

  .login-card-content {
    padding: 28px 22px !important;
  }

  .login-heading {
    margin-bottom: 26px;
  }

  .login-heading-actions {
    width: 150px;
  }

  .captcha-row {
    grid-template-columns: 1fr 112px;
    gap: 8px;
  }
}
</style>
